import React, { Fragment, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import useSlideUploader from './hooks/useSlideUploader';
import Button from '../../../Button';
import { fileInputAccept } from '../../../../../constants/photo-upload';
import UploaderRecommendations from '../../../../../apps/pro/components/UploaderRecommendations';
import { photoUploadFilter } from '../../../../../util/photo-upload';
import {
  accountFromContributorUploadInfo,
  uploadBlockedByPhotoFinderCredits,
  uploadBlockedByStorageLimit,
} from '../../../../../apps/pro/views/AlbumFolder/AlbumFolderUpload/helpers';
import UploadProgressTracking from '../../../../../apps/pro/components/UploadProgressTracking';
import useOpenModal from '../../../../../hooks/useOpenModal';
import useCloseModal from '../../../../../hooks/useCloseModal';
import useOpenAlert from '../../../../../hooks/useOpenAlert';
import { PHOTO_MANAGER_SUBSCRIPTION_CHECKOUT_MODAL } from '../../../../../apps/pro/views/Account/AccountPhotoManagerSettings/components/PhotoManagerSubscriptionCheckout/modal';
import { PHOTO_FINDER_SUBSCRIPTION_CHECKOUT_MODAL } from '../../../../../apps/pro/views/Account/AccountPhotoManagerSettings/components/PhotoFinderSubscriptionCheckout/modal';
import { PHOTO_MANAGER_CREDIT_CARD_CAPTURE_MODAL } from '../../../../../apps/pro/views/Album/AlbumCreateWizard/AlbumCreateDeliveryPreferenceStep/PhotoManagerCreditCardCaptureModal';
import { getErrorString } from '../../../../../util/normalize-errors';
import { isProContent } from '../../../../../util/album';

const propTypes = {
  albumId: PropTypes.string.isRequired,
  onUploadSuccess: PropTypes.func.isRequired,
};

const SlideUploader = ({ albumId, onUploadSuccess }) => {
  const {
    user,
    isAllowed,
    isContributor,
    contributorAssignment,
    album,
    albumFolder,
    files,
    complete,
    uploadReset,
    onPhotosUpload,
    onSlidesCreate,
    onSlideshowContributeSend,
    onAutoContributorJoin,
  } = useSlideUploader({
    albumId,
  });
  const inputRef = useRef();
  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const openAlert = useOpenAlert();

  const onPhotosUploadCompleted = async () => {
    try {
      await onSlidesCreate();
      uploadReset();
      onUploadSuccess();
    } catch (e) {
      openAlert(getErrorString(e));
    }
  };

  useEffect(() => {
    uploadReset();

    if (isContributor) {
      // When this mounts, send the contributor an SMS with the contribute link
      onSlideshowContributeSend?.();
    }

    if (!isAllowed) {
      onAutoContributorJoin?.(albumId);
    }
  }, []);

  useEffect(() => {
    if (complete) {
      onPhotosUploadCompleted();
    }
  }, [complete]);

  const onFilesUpload = uploadedFiles => {
    const validFiles = uploadedFiles.filter(photoUploadFilter);

    if (!validFiles.length) {
      return; // Do nothing.
    }

    onPhotosUpload(validFiles);
  };

  const onFileInputChange = event => {
    const uploadedFiles = [...event.target.files];

    const validFiles = uploadedFiles.filter(photoUploadFilter);

    if (!validFiles.length) {
      return; // Do nothing.
    }

    // Prepare the account item:
    const ownerAccountUploadInfo = accountFromContributorUploadInfo(
      contributorAssignment,
    );

    const openPhotoManagerModal = () => {
      const activePhotoManagerSub =
        user.photo_manager_subscriptions?.records?.[0];
      const hasActivePhotoManagerSub = !!activePhotoManagerSub;
      const isFreeTrial = activePhotoManagerSub?.trial_days > 0;

      if (hasActivePhotoManagerSub && !isFreeTrial) {
        openModal(PHOTO_MANAGER_SUBSCRIPTION_CHECKOUT_MODAL, {
          title: 'You are exceeding your storage limit!',
          content: (
            <p>
              To continue uploading content, you need to increase your storage
              limit.
            </p>
          ),
          photoManagerSubscription: activePhotoManagerSub,
          onCheckoutSuccess: () => {
            onFilesUpload(uploadedFiles);
          },
        });
      } else {
        openModal(PHOTO_MANAGER_SUBSCRIPTION_CHECKOUT_MODAL, {
          title: 'You are exceeding your free trial storage limit!',
          content: (
            <p>
              To continue uploading photos and videos, you need to convert your
              free trial to a paid subscription.
            </p>
          ),
          photoManagerSubscription: activePhotoManagerSub,
          onCheckoutSuccess: () => {
            onFilesUpload(uploadedFiles);
          },
        });
      }
    };

    // Only block upload if this is a PRO entity.
    // Shutterbug users do not have their storage accounted for nor
    // are they able to proceed with photo finder credits purchases.
    const isProEntity = isProContent(albumFolder);

    if (
      isProEntity &&
      uploadBlockedByPhotoFinderCredits({
        account: user,
        albumFolder,
        uploadLength: validFiles.length,
        targets: album ? [album] : [],
      }) &&
      !isContributor
    ) {
      openModal(PHOTO_FINDER_SUBSCRIPTION_CHECKOUT_MODAL, {
        title:
          "Your upload will exceed your Photo Finder subscription's limit for photo matches!",
        content: (
          <p>
            To continue uploading photos for matching, you can upgrade your
            Photo Finder subscription or pay per excess photo for the coverage.
          </p>
        ),
        onCheckoutSuccess: () => {
          if (isProEntity && uploadBlockedByStorageLimit({ account: user })) {
            openPhotoManagerModal();
          } else {
            onFilesUpload(uploadedFiles);
          }
        },
        onPayPerPhoto: () => {
          if (user.default_payment_method) {
            closeModal(PHOTO_FINDER_SUBSCRIPTION_CHECKOUT_MODAL);
            if (isProEntity && uploadBlockedByStorageLimit({ account: user })) {
              openPhotoManagerModal();
            } else {
              onFilesUpload(uploadedFiles);
            }
          } else {
            openModal(PHOTO_MANAGER_CREDIT_CARD_CAPTURE_MODAL, {
              title: <h3>Add a Credit Card</h3>,
              description: (
                <p>
                  You will be charged for uploads on your next billing cycle.
                </p>
              ),
              buttonLabel: 'Add Credit Card',
              onSuccess: () => {
                closeModal(PHOTO_FINDER_SUBSCRIPTION_CHECKOUT_MODAL);
                if (
                  isProEntity &&
                  uploadBlockedByStorageLimit({ account: user })
                ) {
                  openPhotoManagerModal();
                } else {
                  onFilesUpload(uploadedFiles);
                }
              },
            });
          }
        },
      });
      return;
    }

    if (
      isProEntity &&
      uploadBlockedByStorageLimit({ account: user }) &&
      !isContributor
    ) {
      openPhotoManagerModal();
      return;
    }

    if (isContributor && ownerAccountUploadInfo) {
      if (
        isProEntity &&
        uploadBlockedByStorageLimit({ account: ownerAccountUploadInfo })
      ) {
        openAlert({
          message: 'There was a problem while attempting to upload photos.',
          info:
            'Storage limit exceeded, please ask the owner of the event to visit their Subscription tab and increase their storage limit.',
        });
        return;
      }

      if (
        isProEntity &&
        uploadBlockedByPhotoFinderCredits({
          account: ownerAccountUploadInfo,
          albumFolder,
          uploadLength: validFiles.length,
          targets: album ? [album] : [],
        })
      ) {
        openAlert({
          message: 'There was a problem while attempting to upload photos.',
          info:
            'Photo finder credits exceeded, please ask the owner of the event to visit their Subscription tab and increase their credits.',
        });
        return;
      }
    }

    onFilesUpload(uploadedFiles);
  };

  return (
    <Fragment>
      {files.length === 0 && (
        <Fragment>
          <label htmlFor="uploader" className="uploader">
            <p className="mb-2 d-none text-lg d-md-block font-weight-bold">
              Drag &amp; drop photos here or select your files
            </p>

            <Button className="uploader-button mb-3" btnSize="lg">
              Select photos
            </Button>

            <input
              id="uploader"
              type="file"
              accept={fileInputAccept}
              onChange={onFileInputChange}
              multiple
              ref={inputRef}
            />

            <UploaderRecommendations />
          </label>
        </Fragment>
      )}
      {files.length > 0 && (
        <UploadProgressTracking
          forceOpen
          className="z-5 mt-4"
          onRetryBtnClick={failedPhotos => onPhotosUpload(failedPhotos)}
          fileLabel="video"
          cancelEnabled
        />
      )}
    </Fragment>
  );
};

SlideUploader.propTypes = propTypes;

export default SlideUploader;
